// Notepad / Notes — only real backend fields (notepadSchema + /api/notepad/notes).
// The frontend NEVER sends user_id / created_at / _id (NP-1 hardening; backend
// mass-assignment defect is documented for the stabilization patch, not exploited).

export type AccessControl = 'private' | 'user' | 'department';

// ── Sticky-note canvas (additive) ─────────────────────────────────────────────
// Persisted inside the existing `notepadSchema.elements` array (no backend
// change — the save DAO already round-trips `elements`). Each element is a
// draggable sticky note that may carry free text and/or a checklist.
export type StickyColor = 'yellow' | 'green' | 'pink' | 'purple' | 'blue';

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

export interface StickyElement {
  id: string;
  type: 'sticky';
  x: number;
  y: number;
  w: number;
  h: number;
  color: StickyColor;
  text: string;
  items?: ChecklistItem[];
}

/** Defensively coerce the backend's `unknown[]` elements into StickyElements. */
export function parseStickies(elements: unknown[] | undefined): StickyElement[] {
  if (!Array.isArray(elements)) return [];
  const colors: StickyColor[] = ['yellow', 'green', 'pink', 'purple', 'blue'];
  return elements
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object' && (e as { type?: string }).type === 'sticky')
    .map((e, i) => ({
      id: typeof e.id === 'string' ? e.id : `sticky_${i}_${Math.random().toString(36).slice(2, 8)}`,
      type: 'sticky' as const,
      x: Number(e.x) || 24,
      y: Number(e.y) || 24,
      w: Number(e.w) || 220,
      h: Number(e.h) || 200,
      color: colors.includes(e.color as StickyColor) ? (e.color as StickyColor) : 'yellow',
      text: typeof e.text === 'string' ? e.text : '',
      items: Array.isArray(e.items)
        ? e.items
            .filter((it): it is Record<string, unknown> => !!it && typeof it === 'object')
            .map((it, j) => ({
              id: typeof it.id === 'string' ? it.id : `item_${j}_${Math.random().toString(36).slice(2, 8)}`,
              text: typeof it.text === 'string' ? it.text : '',
              done: !!it.done,
            }))
        : undefined,
    }));
}

export interface Note {
  id: string;
  title: string;
  notebookText?: string;
  /** Stored by the backend but not edited in this phase (round-tripped untouched). */
  elements?: unknown[];
  drawing?: string;
  access_control: AccessControl;
  /** ObjectId strings of users a `user`-scoped note is shared with. */
  shared_with_users?: string[];
  /** Department keys a `department`-scoped note is shared with. */
  shared_with_depts?: string[];
  /** Owner display name (backend-populated). */
  author_name?: string;
  /** Owner — populated object in list responses; id string in mutation responses. */
  user_id?: string | { _id?: string; id?: string; display_name?: string };
  created_at?: string;
  updated_at?: string;
}

/** ONLY editable fields are ever submitted. */
export interface SaveNotePayload {
  title: string;
  notebookText?: string;
  /** Sticky-note canvas elements (additive; persisted by the existing save DAO). */
  elements?: StickyElement[];
  access_control: AccessControl;
  shared_with_users?: string[];
  shared_with_depts?: string[];
}

/** Robustly extract the owner id whether `user_id` is populated or a raw id. */
export function getOwnerId(u: Note['user_id']): string {
  if (!u) return '';
  if (typeof u === 'string') return u;
  return u.id ?? u._id ?? '';
}
